import React from 'react';

// Gear teeth component for Antikythera mechanical styling
const GearTeeth = ({ center, radius, teethCount, toothHeight, toothWidth, color = '#8B6914', opacity = 0.8 }) => {
  const teeth = [];
  const angleStep = (2 * Math.PI) / teethCount;
  
  for (let i = 0; i < teethCount; i++) {
    const angle = i * angleStep - Math.PI / 2; // Start from top
    const halfWidth = (toothWidth / 2) * (Math.PI / 180);
    
    // Inner points (at radius)
    const innerLeft = {
      x: center + Math.cos(angle - halfWidth) * radius,
      y: center + Math.sin(angle - halfWidth) * radius
    };
    const innerRight = {
      x: center + Math.cos(angle + halfWidth) * radius,
      y: center + Math.sin(angle + halfWidth) * radius
    };
    
    // Outer points (at radius + toothHeight)
    const outerRadius = radius + toothHeight;
    const outerLeft = {
      x: center + Math.cos(angle - halfWidth * 0.6) * outerRadius,
      y: center + Math.sin(angle - halfWidth * 0.6) * outerRadius
    };
    const outerRight = {
      x: center + Math.cos(angle + halfWidth * 0.6) * outerRadius,
      y: center + Math.sin(angle + halfWidth * 0.6) * outerRadius
    };
    
    const pathD = `
      M ${innerLeft.x},${innerLeft.y}
      L ${outerLeft.x},${outerLeft.y}
      L ${outerRight.x},${outerRight.y}
      L ${innerRight.x},${innerRight.y}
      Z
    `;
    
    teeth.push(
      <path
        key={i}
        d={pathD}
        fill={color}
        opacity={opacity}
        stroke="#5C4A1F"
        strokeWidth="0.5"
      />
    );
  }
  
  return <g data-testid="gear-teeth" style={{ pointerEvents: 'none' }}>{teeth}</g>;
};

// Decorative rivets/bolts
const Rivets = ({ center, radius, count, size = 3, color = '#A08040' }) => {
  const rivets = [];
  const angleStep = (2 * Math.PI) / count;
  
  for (let i = 0; i < count; i++) {
    const angle = i * angleStep - Math.PI / 2;
    const x = center + Math.cos(angle) * radius;
    const y = center + Math.sin(angle) * radius;
    
    rivets.push(
      <g key={i}>
        {/* Rivet base */}
        <circle
          cx={x}
          cy={y}
          r={size}
          fill={color}
          stroke="#5C4A1F"
          strokeWidth="0.5"
        />
        {/* Rivet highlight */}
        <circle
          cx={x - size * 0.2}
          cy={y - size * 0.2}
          r={size * 0.3}
          fill="#D4B896"
          opacity="0.6"
        />
      </g>
    );
  }
  
  return <g data-testid="rivets" style={{ pointerEvents: 'none' }}>{rivets}</g>;
};

// Decorative engraved circles
const EngravedRing = ({ center, radius, strokeWidth = 1, dashed = false }) => {
  return (
    <circle
      cx={center}
      cy={center}
      r={radius}
      fill="none"
      stroke="#6B5510"
      strokeWidth={strokeWidth}
      strokeDasharray={dashed ? "2 4" : "none"}
      opacity="0.5"
      style={{ pointerEvents: 'none' }}
    />
  );
};

// Mechanical spoke/arm element
const MechanicalSpokes = ({ center, innerRadius, outerRadius, count, width = 2 }) => {
  const spokes = [];
  const angleStep = (2 * Math.PI) / count;
  
  for (let i = 0; i < count; i++) {
    const angle = i * angleStep - Math.PI / 2;
    const x1 = center + Math.cos(angle) * innerRadius;
    const y1 = center + Math.sin(angle) * innerRadius;
    const x2 = center + Math.cos(angle) * outerRadius;
    const y2 = center + Math.sin(angle) * outerRadius;
    
    spokes.push(
      <line
        key={i}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#8B6914"
        strokeWidth={width}
        opacity="0.4"
      />
    );
  }
  
  return <g data-testid="mechanical-spokes" style={{ pointerEvents: 'none' }}>{spokes}</g>;
};

export { GearTeeth, Rivets, EngravedRing, MechanicalSpokes };
